import { type LangfuseColumnDef } from "@/src/components/table/types";
import {
  type FilterState,
  type ColumnDefinition,
  type OrderByState,
} from "@langfuse/shared";
import { normalizeFilterColumnNames } from "@/src/features/filters/lib/filter-transform";
import type { FilterStateMigration } from "@/src/features/filters/lib/filter-config";

/**
 * Validates if an orderBy state references valid columns.
 * Normalizes legacy column IDs (e.g. "name" → "traceName") via aliases.
 */
export function validateOrderBy(
  orderBy: OrderByState | null,
  columns?: LangfuseColumnDef<any, any>[],
  filterColumnDefinitions?: ColumnDefinition[],
): OrderByState | null {
  if (!orderBy || !columns || columns.length === 0) return null;

  // Flatten group columns: a sortable column can live inside a group def
  // (e.g. the events table's totalTokens under "Usage"), and a flat lookup
  // would silently drop a saved orderBy that references it.
  const flatColumns = columns.flatMap(function flatten(
    col: LangfuseColumnDef<any, any>,
  ): LangfuseColumnDef<any, any>[] {
    return [col, ...(col.columns?.flatMap(flatten) ?? [])];
  });

  const isSortableColumn = (columnId: string) =>
    flatColumns.some(
      (col) => col.id === columnId && col.enableSorting !== false,
    );

  // If the column already exists in the active table, keep it.
  if (isSortableColumn(orderBy.column)) {
    return orderBy;
  }

  // Resolve legacy/canonical IDs via filter-layer aliases (e.g. "name" ↔ "traceName")
  let resolvedColumn: string | null = null;
  if (filterColumnDefinitions) {
    const colDef = filterColumnDefinitions.find(
      (c) =>
        c.id === orderBy.column ||
        c.name === orderBy.column ||
        c.aliases?.includes(orderBy.column),
    );
    if (colDef) {
      const candidates = [colDef.id, ...(colDef.aliases ?? [])];
      resolvedColumn =
        candidates.find((candidate) => isSortableColumn(candidate)) ?? null;
    }
  }

  return resolvedColumn ? { ...orderBy, column: resolvedColumn } : null;
}

/**
 * Validates if filters reference valid columns and normalizes column names to IDs
 * - Old system: filters used display names (e.g., "User ID", "Name", "⭐️")
 * - New system: filters use column IDs (e.g., "userId", "name", "bookmarked")
 *
 * Switched to IDs because better:
 * - Display names can change for UX or i18n (in future), IDs are stable
 * - IDs match database field names and rest of codebase
 * - Special characters in display names can cause issues
 * - Better type safety and predictability
 *
 * Here, we:
 * - validate that filter columns exist
 * - normalize old display names to new IDs
 * - filter out invalid/deleted columns
 */
export function validateFilters(
  filters: FilterState,
  filterColumnDefinition?: ColumnDefinition[],
  migrateFilterState?: FilterStateMigration,
): FilterState {
  if (!filterColumnDefinition || filterColumnDefinition.length === 0)
    return filters;

  // Normalize display names to column IDs for backward compatibility
  const normalized = normalizeFilterColumnNames(
    filters,
    filterColumnDefinition,
  );
  const migrated = migrateFilterState
    ? migrateFilterState(normalized)
    : normalized;

  // Validate that columns exist (remove invalid ones)
  // After normalization, filter.column is always a canonical ID
  return migrated.filter((filter) => {
    return filterColumnDefinition.some((def) => def.id === filter.column);
  });
}
